import { ReactElement, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlinePencilSquare,
  HiOutlineArrowPath,
  HiOutlineTrash,
  HiOutlineEyeSlash,
  HiOutlineArrowRight,
  HiOutlineDocumentText,
} from 'react-icons/hi2';
import Button from '../../../components/Button';
import DropdownPortal from '../../../components/DropdownPortal';
import ConfigSelector from './ConfigSelector';
import { patchRun } from '../../../api/runs';
import { toast } from 'react-hot-toast';
import { useReprocess } from '../../../hooks/useReprocess';
import { SessionAnalysisType, SessionAnalysisTypeUtils } from '../../../types/SessionAnalysisType';
import { getDiscardConfig, isDiscardConfig } from '../../../utils/configRole';
import { getSingletonConfigId, useRefreshSingletonSessions } from '../../../hooks/useSingletonSessions';
import { EditOperationMode } from '../../SessionView/SessionDetails/SessionConfigs/editOperationUtils';
import { toastMoveResult, useRunConfigMove } from './useRunConfigMove';

interface RunEditMenuProps {
  run: any;
  config: any;
  configs?: any[];
  session?: any;
  sessions?: any[] | null;
  sessionsLoading?: boolean;
  invalidateRuns: any;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  moveRight?: boolean;
  buttonStyle: string;
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
  onReprocessRun?: () => void;
}

export default function RunEditMenu(props: RunEditMenuProps): ReactElement {
  const {
    run,
    config,
    configs,
    session,
    sessions,
    sessionsLoading,
    invalidateRuns,
    configMovesToSave,
    moveRight = false,
    buttonStyle,
    editOpMode,
    setEditOpMode,
    onReprocessRun,
  } = props;

  const navigate = useNavigate();
  const [showEditMenu, setShowEditMenu] = useState(false);
  const editMenuRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { handleReprocess } = useReprocess();
  const refreshSingletonSessions = useRefreshSingletonSessions();
  const { moveRunToConfig, isDeferred } = useRunConfigMove({
    run,
    config,
    configs,
    sessions,
    invalidateRuns,
    configMovesToSave,
    editOpMode,
    setEditOpMode,
  });

  // Helper function to handle singleton config fetching with retry on error
  const getSingletonConfigWithRetry = async (analysisType: SessionAnalysisType, retryCount = 0): Promise<string> => {
    try {
      return await getSingletonConfigId(analysisType);
    } catch (error) {
      // If first attempt failed, refresh cache and retry once
      if (retryCount === 0) {
        console.warn('Failed to get singleton config, refreshing cache and retrying...', error);
        await refreshSingletonSessions();
        return getSingletonConfigWithRetry(analysisType, 1);
      }
      // If retry also failed, throw the error
      throw error;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editMenuRef.current && !editMenuRef.current.contains(event.target as Node)) {
        setShowEditMenu(false);
      }
    };

    if (showEditMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEditMenu]);

  const handleReprocessRun = async () => {
    if (!run.id) {
      toast.error('Cannot reprocess planned runs');
      return;
    }

    if (!handleReprocess) {
      toast.error('Reprocessing function not available');
      return;
    }

    try {
      await handleReprocess(invalidateRuns, [run]);
      setShowEditMenu(false);
      if (onReprocessRun) onReprocessRun();
    } catch (error) {
      console.error('Reprocessing error:', error);
      toast.error('Error reprocessing run');
    }
  };

  // Two different things are called "discarded": every session owns a Discards
  // group (letter_number DISC), and the team owns a Discarded singleton session.
  // A run sitting in a real session belongs in that session's own Discards group
  // — sending it to the singleton takes it out of the session altogether, which
  // is what `removeFromSession` is for. SessionDetails hands down `configs`;
  // the cross-session run list has to look the session up in `sessions`.
  const runSessionId = config?.testsession_id ?? session?.id ?? null;
  const ownSessionConfigs: any[] | undefined = configs
    ?? sessions?.find((s: any) => s.id === runSessionId)?.configs;
  const sessionDiscardConfig = getDiscardConfig<any>(ownSessionConfigs);

  const moveToDiscarded = async () => {
    try {
      // Every session is created with a Discards group, so a run that belongs to
      // one always has somewhere to go. If the group can't be found, that is a
      // fault in the session's config set — surface it rather than routing round
      // it, because the only route round is the team singleton, which would
      // quietly evict the run from its session and hide the fault.
      if (!sessionDiscardConfig) {
        if (runSessionId) {
          toast.error(ownSessionConfigs
            ? 'This session has no Discards group'
            : 'Session configs not loaded yet — try again');
          return;
        }
        // No session at all (an unallocated run): the singleton is its only home.
        const singletonId = await getSingletonConfigWithRetry(SessionAnalysisType.DISCARDED);
        await patchRun(run.id, { config_id: singletonId });
        invalidateRuns();
        toast.success('Run moved to Discarded');
        return;
      }

      // Within-session move: commit it the way the session editor commits a
      // config change, so an immediate write can't collide with pending edits.
      toastMoveResult(await moveRunToConfig(sessionDiscardConfig.id), 'Discards');
    } catch (error) {
      console.error('Error moving run to discarded:', error);
      toast.error('Error moving run to discarded');
    }
  };

  // The moves below send the run to a singleton session, so it leaves the config
  // list the session editor is holding — there is nothing to stage in the form,
  // and the write plus invalidate has to go out now. Inside SessionDetails that
  // refetch would discard whatever else is unsaved, so refuse while another edit
  // operation is in flight rather than taking those edits down with it.
  const ejectBlocked = (): boolean => {
    if (isDeferred && editOpMode != null && editOpMode !== 'idle') {
      toast.error('Save pending changes first');
      return true;
    }
    return false;
  };

  /** Send the run to the team's singleton session for `analysisType`. */
  const moveToSingleton = async (analysisType: SessionAnalysisType, done: string, failing: string) => {
    if (ejectBlocked()) return;
    try {
      const configId = await getSingletonConfigWithRetry(analysisType);
      await patchRun(run.id, { config_id: configId });
      invalidateRuns();
      toast.success(done);
    } catch (error) {
      console.error(`Error ${failing}:`, error);
      toast.error(`Error ${failing}`);
    }
  };

  const moveToHidden = () =>
    moveToSingleton(SessionAnalysisType.DISCARDED_ARCHIVE, 'Run moved to Hidden', 'moving run to hidden');

  const moveToFreeRideNonSession = () =>
    moveToSingleton(SessionAnalysisType.FREERIDE_UNSTRUCTURED, 'Run moved to Free Ride Non-Session',
      'moving run to free ride non-session');

  const removeFromSession = () =>
    moveToSingleton(SessionAnalysisType.DISCARDED, 'Run removed from session', 'removing run from session');

  const handleNotesClick = () => {
    // Use the same navigation logic as the overview button in RunEntry
    // This ensures the back button returns to the correct context
    if (config?.testsession_id) {
      navigate(`/sessions/${config.testsession_id}/runs/${run.id}/overview`, {
        state: { activateNotesEdit: true },
      });
    } else {
      navigate(`/runs/${run.id}/overview`, {
        state: { activateNotesEdit: true },
      });
    }
    setShowEditMenu(false);
  };

  return (
    <div className="cr-relative" ref={anchorRef}>
      <Button
        className={buttonStyle}
        onClick={() => setShowEditMenu(!showEditMenu)}
        icon={<HiOutlinePencilSquare className="cr-size-5" />}
      />
      <DropdownPortal anchorRef={anchorRef} open={showEditMenu} alignRight={!moveRight} offsetX={32}>
        <div
          className="cr-animate-fade-in cr-w-80 cr-rounded-md cr-bg-white cr-shadow-lg
                     cr-ring-1 cr-ring-black cr-ring-opacity-5"
          ref={editMenuRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cr-py-1" role="menu" aria-orientation="vertical">
            <div
              className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                         hover:cr-bg-cirrusBlue-25"
              role="menuitem"
              onClick={() => {
                setShowEditMenu(false);
                if (config?.testsession_id) {
                  navigate(
                    `/sessions/${config.testsession_id}/runs/${run.id}/overview`
                  );
                } else {
                  navigate(`/runs/${run.id}/overview`);
                }
              }}
            >
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiOutlineDocumentText className="cr-size-4" />
                <span>Details</span>
              </div>
            </div>
            <div className="cr-my-1 cr-border-t cr-border-gray-200"></div>
            <div
              className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                         hover:cr-bg-cirrusBlue-25"
              role="menuitem"
              onClick={handleNotesClick}
            >
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiOutlineDocumentText className="cr-size-4" />
                <span>Add Notes</span>
              </div>
            </div>
            <div className="cr-my-1 cr-border-t cr-border-gray-200"></div>
            <div
              className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                         hover:cr-bg-cirrusBlue-25"
              role="menuitem"
              onClick={() => handleReprocessRun()}
            >
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiOutlineArrowPath className="cr-size-4" />
                <span>Reprocess Run</span>
              </div>
            </div>
            <ConfigSelector
              run={run}
              config={config}
              session={session}
              setShowEditMenu={setShowEditMenu}
              configs={configs}
              sessions={sessions || undefined}
              sessionsLoading={sessionsLoading}
              invalidateRuns={invalidateRuns}
              configMovesToSave={configMovesToSave}
              editOpMode={editOpMode}
              setEditOpMode={setEditOpMode}
            />
            {/* Free Ride Non-Session is the general-purpose home for a run that
                isn't part of a structured test, so the only run it makes no
                sense for is one already there. Offering it from Discarded,
                Hidden and unallocated too gives those runs a one-click way back
                into a usable place — previously the only route out of Hidden
                was the Move-to-Session dropdown. */}
            {session?.analysis_type !== SessionAnalysisType.FREERIDE_UNSTRUCTURED && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  moveToFreeRideNonSession();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineArrowRight className="cr-size-4" />
                  <span>Move to Free Ride (Non-Session)</span>
                </div>
              </div>
            )}
            {/* Move to Discards - show if run is in a valid session and not already discarded */}
            {!isDiscardConfig(config)
              && (session == null || SessionAnalysisTypeUtils.isValid(session.analysis_type)) && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  moveToDiscarded();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineTrash className="cr-size-4" />
                  <span>{sessionDiscardConfig ? 'Move to Discards' : 'Move to Discarded Runs'}</span>
                </div>
              </div>
            )}
            {/* Remove from Session - show if run is in a discarded config */}
            {config && isDiscardConfig(config) && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  removeFromSession();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineTrash className="cr-size-4" />
                  <span>Remove from Session</span>
                </div>
              </div>
            )}
            {/* Move to Hidden - show if run is in the discarded session */}
            {session && SessionAnalysisTypeUtils.isDiscarded(session.analysis_type) && (
              <div
                className="cr-w-full cr-cursor-pointer cr-px-4 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700
                          hover:cr-bg-cirrusBlue-25"
                role="menuitem"
                onClick={() => {
                  moveToHidden();
                  setShowEditMenu(false);
                }}
              >
                <div className="cr-flex cr-items-center cr-gap-2">
                  <HiOutlineEyeSlash className="cr-size-4" />
                  <span>Move to Hidden</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </DropdownPortal>
    </div>
  );
}
